import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { CheckCircle, Clock, FileText, Users, TrendingUp, Stethoscope, Mic, Brain, Shield, Zap } from 'lucide-react';

const VirtualNursingObservation = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);
  // Convert capabilities into journey steps with numbers
  const nursingJourney = [
    {
      icon: TrendingUp,
      title: "Dynamic Assessment",
      description: "AI evaluates patient responses and prioritizes care based on clinical urgency patterns.",
      number: "01"
    },
    {
      icon: FileText,
      title: "Smart Documentation",
      description: "Natural language processing converts conversations into structured EMR notes automatically.",
      number: "02"
    },
    {
      icon: Shield,
      title: "Intelligent Escalation",
      description: "ML models detect complex scenarios and route cases to appropriate specialists with context.",
      number: "03"
    },
    {
      icon: Brain,
      title: "Adaptive Communication",
      description: "Voice analysis adapts style to patient preferences and emotional needs for optimal engagement.",
      number: "04"
    },
    {
      icon: Zap,
      title: "Workflow Optimization",
      description: "Predictive analytics optimize task prioritization while maintaining clinical excellence.",
      number: "05"
    }
  ];

  const clinicalBenefits = [
    "Provides 24/7 patient monitoring and engagement without fatigue",
    "Reduces nursing documentation time by up to 60% with automated notes", 
    "Identifies patient deterioration patterns before clinical symptoms appear",
    "Scales personalized care delivery across entire patient populations",
    "Integrates seamlessly with existing EMR and clinical workflows"
  ];

  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < nursingJourney.length - 1) {
      setTimeout(() => {
        setTypingCardIndex(index + 1);
      }, 300);
    }
  };

  React.useEffect(() => {
    // Start the journey when the section comes into view
    const timer = setTimeout(() => {
      setTypingCardIndex(0);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>AI Nurse - Virtual Nursing & Observation | Scienza Health</title>
        <meta 
          name="description" 
          content="24/7 AI Nurse for virtual nursing observation with voice biomarker analysis, patient engagement, symptom assessment, and clinical documentation." 
        />
      </Helmet>

      <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--gradient-depth)' }}>
        {/* Neural Background */}
        <NeuralBackground />
        
        {/* Hero Section */}
        <section className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 bg-primary/10 backdrop-blur-md border border-primary/20 rounded-full mb-8">
                <Stethoscope className="w-12 h-12 text-primary" />
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                Digital Human® AI Nurse
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                  Virtual Nursing & Observation
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
                Our AI Nurse engages patients through natural voice interactions—conducting check-ins, 
                symptom assessments, and real-time clinical documentation. Available 24/7.
              </p>
            </motion.div>

            {/* Voice Interaction Showcase */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative max-w-4xl mx-auto mb-20"
            >
              <div className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <Mic className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-semibold text-white">
                      <SynapticText 
                        text="Voice-Powered Clinical Intelligence" 
                        delay={300}
                      />
                    </h3>
                  </div>
                  <SynapticText 
                    text="Through advanced speech biomarker analysis and natural language understanding, our AI Nurse transforms routine patient interactions into comprehensive clinical insights, enabling proactive care delivery at unprecedented scale."
                    delay={1500}
                    className="text-lg text-white leading-relaxed"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="How Digital Human® AI Nurse Works" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Follow our Digital Human® AI nurse through a complete patient care journey"
                delay={1200}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {nursingJourney.map((step, index) => (
                <JourneyCard
                  key={step.title}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  number={step.number}
                  index={index}
                  shouldStartTyping={index === typingCardIndex}
                  onComplete={() => handleCardComplete(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Clinical Impact Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
                <SynapticText 
                  text="Clinical Benefits" 
                  delay={200}
                />
              </h2>
              
              <div className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
                
                <div className="relative z-10 space-y-8">
                  {clinicalBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.6, 
                        delay: 0.3 + index * 0.2,
                        ease: "easeOut"
                      }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-lg sm:text-xl text-white leading-relaxed">
                        <SynapticText 
                          text={benefit}
                          delay={600 + index * 400}
                          className="text-lg sm:text-xl text-white leading-relaxed"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Ready to Deploy Digital Human® AI Nursing?
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto">
                Experience the future of patient care with voice-powered clinical intelligence 
                that never sleeps, never forgets, and continuously learns.
              </p>
              
              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Stethoscope className="w-5 h-5 mr-3" />
                Schedule Clinical Demo
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default VirtualNursingObservation;